import { useState, type MouseEvent } from 'react';
import { brandify } from '../Brand';

// Order: lead with the questions a visitor naturally asks after reading the page.
// The comparison and the more technical / reassurance questions sit below "See more".
const FAQS = [
  {
    q: 'Is reeve a replacement for a lawyer or an HR consultant?',
    a: 'reeve is an assistant, not a law firm. It gives you guidance and context grounded in current UK employment law and your own documents, rather than formal legal advice, so you can understand your situation and choose your next step. Think of it as a consultant in your pocket, not an agent. It helps you decide what to do, but it doesn’t act on your behalf. When something calls for a person, you can bring in a CIPD-qualified expert.',
  },
  {
    q: 'What kinds of HR situations can reeve help with?',
    a: 'Everyday questions and the stressful moments alike. reeve helps with employment contracts and policies, a grievance or disciplinary, a probation exit, a redundancy, a dismissal, and the day-to-day ‘what do I do now?’ questions. It gives you the next safe step for your situation, and hands the complex cases to a CIPD-qualified expert.',
  },
  {
    q: 'Who is reeve for?',
    a: 'reeve is built for UK businesses with no HR function, typically from zero to ten employees: founders, owner-managers and small teams where HR questions land on whoever happens to be nearest. If sorting HR keeps stealing hours from the actual business, that’s exactly who we built it for.',
  },
  {
    q: 'Does reeve tell me which HR documents my business needs?',
    a: 'Yes. reeve builds you a tailored list of the required documents and policies for a business your size and sector, split into must-have and should-have, and checks each one as you add it. That way, ‘compliant’ becomes something you can see at any time, not a hopeful guess.',
  },
  {
    q: 'What if I don’t have any HR documents yet?',
    a: 'That’s fine, and it’s common. reeve gives you templates and walks you through completing them so they genuinely fit your business, and a professional can step in if something is particularly complicated. Most companies can get compliant in minutes, even starting from scratch.',
  },
  {
    q: 'Can I talk to a real, CIPD-qualified HR expert when I need one?',
    a: 'Yes. For anything serious, like a grievance, a redundancy or a tricky dismissal, you can escalate the case to a CIPD-qualified professional who gives you hands-on help. You only pay for expert time when you actually use it.',
  },
  {
    q: 'How much does reeve cost?',
    a: 'There’s a free tier that gives you your tailored document requirements, and paid plans from £50 a month that do the checking for you, watch the law and answer your questions. Expert help from a CIPD-qualified professional is available on demand, so you only pay for a human when you need one.',
  },
  {
    q: 'Do I need any HR or legal knowledge to use reeve?',
    a: 'None. reeve tells you what you need in plain English, checks your documents for you and explains your options without the jargon.',
  },
  {
    q: 'Can reeve check my employment contracts and policies?',
    a: 'Yes. reeve reads your own documents and checks them against what UK law and good practice require, flagging anything that’s missing, out of date or too risky to rely on. If something is missing, it can give you a compliant template to start from.',
  },
  {
    q: 'Can I start before I hire my first employee?',
    a: 'Yes. reeve’s free list of required documents and policies gets your contractor agreements and pre-hire paperwork right, so the day you make your first hire you’re already on solid ground rather than scrambling.',
  },
  {
    q: 'Does reeve keep me up to date with the Employment Rights Act 2025?',
    a: 'Yes. The Employment Rights Act 2025 is the biggest change to UK employment law in a generation, with reforms phasing in through 2026 and 2027. reeve tracks each change and tells you exactly which of your documents and processes need updating, and when, so you don’t have to follow it yourself.',
  },
  {
    q: 'How does reeve make sure its answers are accurate and current?',
    a: 'reeve answers from a curated, continuously maintained knowledge base of UK employment law and good practice, combined with your own documents, rather than the open internet. High-stakes situations are pointed to a qualified human rather than left to the AI alone, and keeping that knowledge base current as the law changes is the core of what we do.',
  },
  {
    q: 'How is reeve different from a traditional HR provider like Peninsula or Citation?',
    a: 'reeve automatically checks your organisation’s documents to keep you compliant, and gives you a digital assistant that’s ready the moment a question comes up. You only escalate to a CIPD-qualified professional when a situation gets genuinely complex, so you’re not paying for a full human retainer you rarely use. Traditional providers sell that retainer up front, often on multi-year contracts with per-employee pricing. reeve takes the same headache off your plate for a fraction of the cost.',
  },
  {
    q: 'Does reeve replace my HR or payroll software?',
    a: 'No. reeve isn’t a system for payroll, holidays or absence tracking, and it doesn’t need one to work. It focuses on knowing what you need, checking your documents and answering your questions. Integrations with HR and payroll tools are on the roadmap for later.',
  },
  {
    q: 'Which parts of the UK does reeve cover?',
    a: 'reeve covers employment law in England, Scotland and Wales. Employment law in Northern Ireland is devolved and works differently, so it sits outside our scope for now.',
  },
  {
    q: 'Can I cancel any time?',
    a: 'Yes. Paid plans are billed monthly and you can cancel whenever you like. The free tier stays free for as long as you want it.',
  },
  {
    q: 'Is my data secure, and who can see it?',
    a: 'Your documents and employee records are stored encrypted, and your conversations with the assistant are encrypted too and set to expire by default. Nobody at reeve is trawling through your files. Your data is used only to answer your questions and keep your requirements current.',
  },
  {
    q: 'Can an employee ask to see what I’ve written about them?',
    a: 'Under UK GDPR, an employee can make a subject access request (SAR) for the personal data you hold about them, which can include notes and messages written about them. Because reeve conversations expire by default, there’s usually nothing left to disclose, and you stay in control of anything you deliberately choose to keep.',
  },
  {
    q: 'Do you use my data to train AI models?',
    a: 'Never. We don’t use your documents, employee data or conversations to train AI models. Your information is used only to answer your questions and keep your requirements up to date.',
  },
];

const VISIBLE_BY_DEFAULT = 8;

// FAQ structured data so search engines can parse the Q&As (all of them, including
// the ones tucked behind "see more"; they're in the DOM, just visually collapsed).
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
};

export default function Faq() {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = FAQS.length - VISIBLE_BY_DEFAULT;

  // Native <details> only toggles when the <summary> is clicked. Let a click
  // anywhere on the card toggle it too. Clicks on the summary keep their native
  // behaviour, so the FAQ still works with JavaScript disabled.
  const handleCardClick = (e: MouseEvent<HTMLDetailsElement>) => {
    const details = e.currentTarget;
    const summary = details.querySelector('summary');
    if (summary && !summary.contains(e.target as Node)) {
      details.open = !details.open;
    }
  };

  return (
    <section className="section container">
      <h2 className="faq__title">Frequently asked questions</h2>
      <div id="faq-grid" className={`faq__grid${expanded ? ' is-expanded' : ''}`}>
        {FAQS.map((item, i) => (
          <details
            className={`faq__item${i >= VISIBLE_BY_DEFAULT ? ' faq__item--extra' : ''}`}
            key={item.q}
            onClick={handleCardClick}
          >
            <summary className="faq__q">{brandify(item.q)}</summary>
            <p className="faq__a">{brandify(item.a)}</p>
          </details>
        ))}
      </div>

      {hiddenCount > 0 && (
        <button
          type="button"
          className="faq__more"
          aria-expanded={expanded}
          aria-controls="faq-grid"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'See more'}
        </button>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </section>
  );
}
